import tokens from "../../../../tokens.json";
import { CAT_DEFAULT, CATEGORY_PALETTE } from "@/lib/chipTextColor";
import { textShadeOn } from "@/lib/userColorText";

/// Resolve a dataset's stored palette short-name (a `cat-*` key without the
/// prefix, e.g. "sky") to a theme-correct color. Reads the generated
/// `--token-color-cat-*` CSS var so the value tracks light/dark and matches the
/// categorical palette used everywhere else. Falls back to `cat-default` for an
/// empty or unknown token.
export function resolveDatasetColor(token: string | null | undefined): string {
  const style = getComputedStyle(document.documentElement);
  const key = token && token.length > 0 ? `cat-${token}` : "cat-default";
  const resolved = style.getPropertyValue(`--token-color-${key}`).trim();
  return resolved || style.getPropertyValue("--token-color-cat-default").trim();
}

/// `resolveDatasetColor` as a CSS value: no style read, and it follows a theme
/// switch. A name that is not a palette key falls back to the default.
export function datasetColorVar(token: string | null | undefined): string {
  const key = token && /^[a-z0-9-]+$/.test(token) ? token : "default";
  return `var(--token-color-cat-${key}, var(--token-color-cat-default))`;
}

/// The darkest light surface the text sits on: a hovered row or control.
const LIGHT_SURFACE = tokens.shared.colors["bg-hover"].light;

/// The palette as text: its fills are too faint to read, so text takes the
/// other theme's variant, darkened in light until it keeps 4.5:1.
export function datasetTextColor(
  token: string | null | undefined,
  isDark: boolean,
): string {
  const entry = CATEGORY_PALETTE.find((p) => p.name === token) ?? CAT_DEFAULT;
  if (isDark) return entry.light;
  return textShadeOn(entry.dark, LIGHT_SURFACE) ?? entry.dark;
}
